import {
  AdvisorInfoWithWritingCode,
  checkForSpecificConditions,
  checkMedicalApplianceUsage,
  convertHeightToFeetAndInches,
  formatDateForForm,
  GetAdvisorAddress,
  mapGender,
  mapMaritalStatus,
  PARTNER_PREFIX,
  returnSafeString,
} from './carrier-utils';

export const ONE_AMERICA_PREFILL_SECTIONS_PRIMARY = [
  'first-proposed-insured',
  'annual-income',
  'medical-health-info-fpi',
  'temporary-insurance-agreement',
  'alcohol-use-supplement',
  'drug-use-supplement',
  'joint-owner',
];

export const ONE_AMERICA_PREFILL_SECTIONS_SECONDARY = [
  'second-proposed-insured',
  'annual-income',
  'medical-health-info-spi',
];

export const ONEAMERICA_UNDERWRITING_PREFILL_SECTIONS = [
  'client-info',
  'cardiac',
  'cancer',
  'diabetes',
  'mental-nervous',
  'substance-abuse',
  'musculoskeletal',
  'osteoporosis',
  'respiratory',
  'stroke-tia',
  'other-impairments',
];

export const getClientUpdatesForOneAmerica = (clientInfo: Client): PreFillUpdate[] => {
  const clientUpdates = [
    {
      questionRef: 'firstProposedInsuredFirstName',
      answer: clientInfo.clientFirstName,
    },
    {
      questionRef: 'firstProposedInsuredLastName',
      answer: clientInfo.clientLastName,
    },
    {
      questionRef: 'firstProposedInsuredEmailAddress',
      answer: clientInfo.clientEmail,
      sourceQuestionRefs: ['clientEmail'],
    },
    {
      questionRef: 'firstProposedInsuredFullName',
      answer: clientInfo.clientFullName,
    },
    {
      questionRef: 'fiDisclosureClientName',
      answer: clientInfo.clientFullName,
    },
    {
      questionRef: 'authDisclosureAttention',
      answer: clientInfo.clientFullName,
    },
    {
      questionRef: 'authDisclosureApplicantPolicyI',
      answer: clientInfo.clientFullName,
    },
    {
      questionRef: 'authDisclosureProposedInsuredAnnuitant',
      answer: clientInfo.clientFullName,
    },
    {
      questionRef: 'policyInformationProposedInsuredDOB',
      answer: formatDateForForm(clientInfo.intakeSurvey?.clientBirthDate),
      sourceQuestionRefs: ['clientBirthDate'],
    },
    {
      questionRef: 'policyInformationProposedInsuredName',
      answer: clientInfo.clientFullName,
    },
    {
      questionRef: 'adcPrintedName',
      answer: clientInfo.clientFullName,
    },
  ].filter(update => update.answer);

  const clientPartnerUpdates = clientInfo.mutableClientPartner
    ? [
        {
          questionRef: 'secondProposedInsuredFirstName',
          answer: clientInfo.mutableClientPartner.clientFirstName,
        },
        {
          questionRef: 'secondProposedInsuredLastName',
          answer: clientInfo.mutableClientPartner.clientLastName,
        },
        {
          questionRef: 'secondProposedInsuredEmailAddress',
          answer: clientInfo.mutableClientPartner.clientEmail,
          sourceQuestionRefs: [PARTNER_PREFIX + 'clientEmail'],
        },
        {
          questionRef: 'secondProposedInsuredFullName',
          answer: clientInfo.mutableClientPartner.clientFullName,
        },
        {
          questionRef: 'adcSpouseName',
          answer: clientInfo.mutableClientPartner.clientFullName,
        },
      ].filter(update => update.answer)
    : [];

  const directMedicalMappings = getDirectMedicalMappings(clientInfo);

  return [...clientUpdates, ...clientPartnerUpdates, ...directMedicalMappings];
};

const getDirectMedicalMappings = (clientInfo: Client): PreFillUpdate[] => {
  let mappings: PreFillUpdate[] = [];

  // Primary client medical mappings
  if (clientInfo?.intakeSurvey) {
    const { intakeSurvey } = clientInfo;
    const heightData = convertHeightToFeetAndInches(intakeSurvey.clientHeightInches);

    const primaryMappings = [
      // Height and Weight mappings
      {
        questionRef: 'medicalFirstProposedInsuredHeightFT',
        answer: heightData.feet > 0 ? heightData.feet : '',
        sourceQuestionRefs: ['clientHeightInches'],
      },
      {
        questionRef: 'medicalFirstProposedInsuredHeightIN',
        answer: heightData.feet > 0 ? heightData.inches : '',
        sourceQuestionRefs: ['clientHeightInches'],
      },
      {
        questionRef: 'medicalFirstProposedInsuredWeight',
        answer:
          (intakeSurvey.clientWeightPounds || 0) > 0
            ? intakeSurvey.clientWeightPounds.toString()
            : '',
        sourceQuestionRefs: ['clientWeightPounds'],
      },

      // Basic demographic information
      {
        questionRef: 'firstProposedInsuredBirthDate',
        answer: formatDateForForm(intakeSurvey.clientBirthDate),
        sourceQuestionRefs: ['clientBirthDate'],
      },
      {
        questionRef: 'firstProposedInsuredGender',
        answer: mapGender(intakeSurvey.clientGenderAtBirth),
        sourceQuestionRefs: ['clientGenderAtBirth'],
      },
      {
        questionRef: 'firstProposedInsuredMarriedStatus',
        answer: mapMaritalStatus(intakeSurvey.clientMaritalStatus),
        sourceQuestionRefs: ['clientMaritalStatus'],
      },
      {
        questionRef: 'secondProposedInsuredRelationship',
        answer: mapMaritalStatus(intakeSurvey.clientMaritalStatus) === 'yes' ? 'Spouse' : '',
        sourceQuestionRefs: ['clientMaritalStatus'],
      },
      {
        questionRef: 'clientIncome',
        answer: returnSafeString(intakeSurvey.householdPriorYearIncome),
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'clientNetWorth',
        answer: returnSafeString(intakeSurvey.householdTotalAssetsValue),
        sourceQuestionRefs: ['householdTotalAssetsValue'],
      },
      {
        questionRef: 'incomeNetWorth',
        answer: returnSafeString(intakeSurvey.householdTotalAssetsValue),
        sourceQuestionRefs: ['householdTotalAssetsValue'],
      },
      {
        questionRef: 'incomeEarned',
        answer: returnSafeString(intakeSurvey.householdPriorYearIncome),
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'ltcHouseholdIncome',
        answer:
          typeof intakeSurvey.householdPriorYearIncome === 'number'
            ? intakeSurvey.householdPriorYearIncome < 10000
              ? 'less-10000'
              : intakeSurvey.householdPriorYearIncome < 20000
                ? '10000-19999'
                : intakeSurvey.householdPriorYearIncome < 30000
                  ? '20000-29999'
                  : intakeSurvey.householdPriorYearIncome <= 50000
                    ? '30000-50000'
                    : 'more-50000'
            : '',
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'ltcAssetsWorth',
        answer:
          typeof intakeSurvey.householdTotalAssetsValue === 'number'
            ? intakeSurvey.householdTotalAssetsValue < 20000
              ? 'less-20000'
              : intakeSurvey.householdTotalAssetsValue < 30000
                ? '20000-29999'
                : intakeSurvey.householdTotalAssetsValue < 50000
                  ? '30000-49999'
                  : intakeSurvey.householdTotalAssetsValue >= 50000
                    ? 'more-50000'
                    : ''
            : '',
        sourceQuestionRefs: ['householdTotalAssetsValue'],
      },

      // Medical condition mappings based on diagnosis lists
      {
        questionRef: 'medicalHeartConditionFirstInsured',
        answer: checkDiagnosisList(intakeSurvey.clientDiagnosesList, 'cardiovascular')
          ? 'yes'
          : 'no',
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCancerFirstInsured',
        answer: checkDiagnosisList(intakeSurvey.clientDiagnosesList, 'cancer') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalDiabetesFirstInsured',
        answer: checkDiagnosisList(intakeSurvey.clientDiagnosesList, 'diabetes') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalLungDisorderFirstInsured',
        answer: checkDiagnosisList(intakeSurvey.clientDiagnosesList, 'respiratory') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalIntestinalFirstInsured',
        answer: checkDiagnosisList(intakeSurvey.clientDiagnosesList, 'digestive') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalBrainNervousFirstInsured',
        answer: checkDiagnosisList(intakeSurvey.clientDiagnosesList, 'neurological') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalArthritisFirstInsured',
        answer: checkDiagnosisList(
          intakeSurvey.clientDiagnosesList,
          'arthritis',
          intakeSurvey.clientInjuriesList,
        )
          ? 'yes'
          : 'no',
        sourceQuestionRefs: ['clientDiagnosesList', 'clientInjuriesList'],
      },
      {
        questionRef: 'medicalEyesEarsFirstInsured',
        answer: checkDiagnosisList(intakeSurvey.clientDiagnosesList, 'eyes_ears') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalKidneyFirstInsured',
        answer: checkDiagnosisList(intakeSurvey.clientDiagnosesList, 'kidney') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDiagnosesList'],
      },

      // Smoking history
      {
        questionRef: 'medicalSmokerFirstInsured',
        answer: intakeSurvey.clientSmokerEver === 'Yes' ? 'yes' : 'no',
        sourceQuestionRefs: ['clientSmokerEver'],
      },

      // Additional medical questionnaire - Section B (Past 5 years)
      {
        questionRef: 'medicalHospitalPatientFirstInsured',
        answer:
          intakeSurvey.clientHospitalizedERLastTwelveMonths &&
          (intakeSurvey.clientHospitalizedERLastTwelveMonths.includes('Hospitalized') ||
            intakeSurvey.clientHospitalizedERLastTwelveMonths.includes('Visited the ER') ||
            intakeSurvey.clientPriorTwoYearHospitalizationCount > 0)
            ? 'yes'
            : 'no',
        sourceQuestionRefs: [
          'clientHospitalizedERLastTwelveMonths',
          'clientPriorTwoYearHospitalizationCount',
        ],
      },

      // Additional medical questionnaire - Section C (Current status)
      {
        questionRef: 'medicalPhysicalTherapyFirstInsured',
        answer: checkTherapyUsage(intakeSurvey.clientTherapyTypes) ? 'yes' : 'no',
        sourceQuestionRefs: ['clientTherapyTypes'],
      },
      {
        questionRef: 'medicalMedicalApplianceFirstInsured',
        answer: checkMedicalApplianceUsage(intakeSurvey.clientAssistiveDevicesList) ? 'yes' : 'no',
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },

      // Drug use and substance questions
      {
        questionRef: 'medicalMarijuanaUseFirstInsured',
        answer: checkSubstanceUsage(intakeSurvey.clientSubstancesUsed, 'marijuana') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'drugUseCannabis',
        answer: checkSubstanceUsage(intakeSurvey.clientSubstancesUsed, 'marijuana') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalHardDrugsFirstInsured',
        answer: checkSubstanceUsage(intakeSurvey.clientSubstancesUsed, 'hard_drugs') ? 'yes' : 'no',
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalDrugUseFirstInsured',
        answer:
          checkSubstanceUsage(intakeSurvey.clientSubstancesUsed, 'marijuana') ||
          checkSubstanceUsage(intakeSurvey.clientSubstancesUsed, 'hard_drugs')
            ? 'yes'
            : 'no',
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalTobaccoUseFirstInsured',
        answer:
          checkSubstanceUsage(intakeSurvey.clientSubstancesUsed, 'tobacco') ||
          intakeSurvey.clientSmokerEver === 'Yes'
            ? 'yes'
            : 'no',
        sourceQuestionRefs: ['clientSmokerEver'],
      },
      {
        questionRef: 'medicalMedicationsFirstInsured',
        answer: parseMedicationsToTableFormat(intakeSurvey.clientMedicationIntake),
        sourceQuestionRefs: ['clientMedicationIntake'],
      },
      {
        questionRef: 'medicalHeartAttackFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'heart attack',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalHighBloodPressureFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'high blood pressure',
          'hypertension',
        ]),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalStrokeFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'Single stroke',
          'Multiple strokes',
          'stroke',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalTransientIschemicAttackTiaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'TIA ("mini stroke")',
          'TIA',
          'Transient Ischemic Attack',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCongestiveHeartFailureFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'Congestive Heart Failure',
          'Cardiomyopathy or congestive heart failure (CHF)',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalIrregularHeartbeatFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'irregular heartbeat',
          'arrhythmia',
          'Atrial fibrillation or other arrhythmia',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAtrialFibrillationFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'irregular heartbeat',
          'arrhythmia',
          'Atrial fibrillation or other arrhythmia',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPeripheralVascularDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'peripheral vascular disease',
          'peripheral artery disease',
          'PAD',
          'Peripheral vascular disease / aneurysm',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalHeartMurmurFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'heart murmur',
          'Valvular Heart Disease',
          'Heart valve disease / murmur',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalValvularHeartDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'heart murmur',
          'Valvular Heart Disease',
          'Heart valve disease / murmur',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCoronaryArteryDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'Coronary Artery Disease',
          'CAD',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalLeukemiaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
          'Leukemia',
          'blood',
          'bone',
        ]),
        sourceQuestionRefs: ['clientCancerTypeLocation', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalLymphomaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, ['Lymphoma']),
        sourceQuestionRefs: ['clientCancerTypeLocation', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalTumorFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
          'Tumor',
          'Mass',
          'Lump',
          'Skin Lesion',
          'Skin Disorder',
          'Skin Cancer',
          'Skin',
        ]),
        sourceQuestionRefs: ['clientCancerTypeLocation', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAsthmaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, ['Asthma']),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalChronicObstructivePulmonaryDiseaseCopdFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'COPD / emphysema',
          'Chronic bronchitis',
          'Chronic lung disorder',
          'Chronic obstructive pulmonary disease (COPD)',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalEmphysemaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'COPD / emphysema',
          'Emphysema',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalSleepApneaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'Sleep apnea',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCrohnsDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          "Crohn's disease",
          "Ulcerative Colitis / Crohn's Disease (IBD)",
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalUlcerativeColitisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'colitis',
          "Ulcerative Colitis / Crohn's Disease (IBD)",
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalHepatitisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'hepatitis',
          'Hepatitis A / B / C',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPancreatitisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, ['pancreatitis']),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalBipolarDisorderFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, [
          'Bipolar Disorder (Manic Depression)',
          'Bipolar Disorder',
          'Bipolar',
        ]),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalDepressionFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, ['Depression']),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAnxietyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, ['Anxiety']),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPostTraumaticStressDisorderFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, [
          'Post-traumatic stress disorder (PTSD)',
        ]),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalSchizophreniaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, [
          'Schizophrenia',
        ]),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalChronicKidneyDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'Chronic kidney disease',
          'Kidney disorder',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalKidneyFailureFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'Kidney failure',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAlzheimerDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientCognitiveMemoryDiagnosis, [
          "Alzheimer's",
        ]),
        sourceQuestionRefs: ['clientCognitiveMemoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalDementiaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientCognitiveMemoryDiagnosis, [
          'Dementia',
        ]),
        sourceQuestionRefs: ['clientCognitiveMemoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalTremorsFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'Tremors',
          'Parkinson',
          'Parkinsonism',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalParkinsonDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'Parkinson',
          'Parkinsonism',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalConvulsionsEpilepsyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'Seizures',
          'Convulsions',
          'Epilepsy / seizures',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalSystemicLupusFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, ['Lupus']),
        sourceQuestionRefs: ['clientImmuneSystemDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalSclerodermaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, [
          'Scleroderma',
        ]),
        sourceQuestionRefs: ['clientImmuneSystemDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalWetMacularDegenerationFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientEENTDiagnosis, [
          'Macular degeneration',
        ]),
        sourceQuestionRefs: ['clientEENTDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAneurysmFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'aneurysm',
          'Peripheral vascular disease / aneurysm',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCarotidArteryDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'carotid artery disease',
          'carotid stenosis',
          'cad',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCardiomyopathyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'Cardiomyopathy',
          'Cardiomyopathy or congestive heart failure (CHF)',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCongenitalHeartDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'congenital heart disease',
          'congenital heart defect',
          'chd',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAtherosclerosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'atherosclerosis',
          'hardening of the arteries',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalVenousInsufficiencyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'venous insufficiency',
          'chronic venous insufficiency',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalDeepVeinThrombosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'deep vein thrombosis',
          'DVT',
          'blood clot',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPulmonaryEmbolismFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
          'pulmonary embolism',
          'PE',
        ]),
        sourceQuestionRefs: ['clientHeartConditionDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalEnlargementOfTheLymphGlandFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
          'lymph gland',
          'lymph node',
          'enlarged lymph',
          'lymphoma',
        ]),
        sourceQuestionRefs: ['clientCancerTypeLocation', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalMultipleMyelomaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
          'multiple myeloma',
          'myeloma',
        ]),
        sourceQuestionRefs: ['clientCancerTypeLocation', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAnemiaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['anemia']),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPolycythemiaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['polycythemia']),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalThrombocytopeniaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'thrombocytopenia',
          'low platelet',
        ]),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPolycysticKidneyDiseaseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'polycystic kidney disease',
          'PKD',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalNephritisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'nephritis',
          'glomerulonephritis',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalNephroticSyndromeFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'nephrotic syndrome',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalHematuriaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'hematuria',
          'blood in urine',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalUrinaryIncontinenceFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'urinary incontinence',
          'bladder incontinence',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalNeurogenicBladderFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
          'neurogenic bladder',
        ]),
        sourceQuestionRefs: ['clientGenitourinaryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalShortnessOfBreathFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'shortness of breath',
          'dyspnea',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPulmonaryFibrosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'pulmonary fibrosis',
          'interstitial lung disease',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalSarcoidosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'sarcoidosis',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPneumoniaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'pneumonia',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalTuberculosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
          'tuberculosis',
          'TB',
        ]),
        sourceQuestionRefs: ['clientLungRespiratoryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalIntestinalBleedingFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'intestinal bleeding',
          'gastrointestinal bleeding',
          'GI bleed',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalUlcerFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'ulcer',
          'peptic ulcer',
          'gastric ulcer',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalGastroparesisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'gastroparesis',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalBarrettEsophagusFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'barrett',
          'esophagus',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCirrhosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'cirrhosis',
          'liver cirrhosis',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalLiverFailureFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, [
          'liver failure',
          'hepatic failure',
        ]),
        sourceQuestionRefs: ['clientDigestiveDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalSuicideAttemptFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, [
          'suicide attempt',
          'suicidal',
        ]),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAnorexiaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, [
          'anorexia',
          'eating disorder',
        ]),
        sourceQuestionRefs: ['clientPsychiatricDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalChronicFatigueFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'chronic fatigue',
          'CFS',
        ]),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalFibromyalgiaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['fibromyalgia']),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalDizzinessFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, ['dizziness']),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalVertigoFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, ['vertigo']),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalMigrainesFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'migraine',
          'migraines',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalTraumaticBrainInjuryFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'traumatic brain injury',
          'TBI',
          'head injury',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalGaitDisturbanceFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'gait disturbance',
          'walking difficulty',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalNeurofibromatosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'neurofibromatosis',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalFaintingFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'fainting',
          'syncope',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalParalysisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'paralysis',
          'paraplegia',
          'quadriplegia',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalMyastheniaGravisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'myasthenia gravis',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPeripheralNeuropathyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'peripheral neuropathy',
          'neuropathy',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalMultipleSclerosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'multiple sclerosis',
          'MS',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalALSFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'ALS',
          'amyotrophic lateral sclerosis',
          "Lou Gehrig's disease",
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalGuillainBarreSyndromeFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
          'guillain-barre',
          'guillain barre',
        ]),
        sourceQuestionRefs: ['clientBrainInjuryDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalLossOfLimbFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, [
          'loss of limb',
          'amputation',
        ]),
        sourceQuestionRefs: ['clientInjuriesList'],
      },
      {
        questionRef: 'medicalOrganTransplantFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'organ transplant',
          'transplant',
        ]),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalMyopathyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, [
          'myopathy',
          'muscle disease',
        ]),
        sourceQuestionRefs: ['clientInjuriesList', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPolymyalgiaRheumaticaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, [
          'polymyalgia rheumatica',
        ]),
        sourceQuestionRefs: ['clientImmuneSystemDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalMuscularDystrophyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, ['muscular dystrophy']),
        sourceQuestionRefs: ['clientInjuriesList', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalVertebralFracturesFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, [
          'vertebral fracture',
          'spinal fracture',
          'compression fracture',
        ]),
        sourceQuestionRefs: ['clientInjuriesList'],
      },
      {
        questionRef: 'medicalOsteoporosisFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['osteoporosis']),
        sourceQuestionRefs: ['clientDiagnosesList'],
      },
      {
        questionRef: 'medicalRetinopathyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientEENTDiagnosis, [
          'retinopathy',
          'diabetic retinopathy',
        ]),
        sourceQuestionRefs: ['clientEENTDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalLossOfVisionFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientEENTDiagnosis, [
          'loss of vision',
          'blindness',
          'vision loss',
        ]),
        sourceQuestionRefs: ['clientEENTDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalKaposiSarcomaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, [
          'kaposi sarcoma',
          "kaposi's sarcoma",
        ]),
        sourceQuestionRefs: ['clientImmuneSystemDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPneumocystisCariniiPneumoniaFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, [
          'pneumocystis',
          'PCP',
        ]),
        sourceQuestionRefs: ['clientImmuneSystemDiagnosis', 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalHospitalTreatmentFirstInsured',
        answer:
          intakeSurvey.clientPriorTwoYearHospitalizationCount > 0 ||
          checkForSpecificConditions(intakeSurvey.clientHospitalizedERLastTwelveMonths, [
            'Hospitalized',
          ]) === 'yes'
            ? 'yes'
            : 'no',
        sourceQuestionRefs: [
          'clientPriorTwoYearHospitalizationCount',
          'clientHospitalizedERLastTwelveMonths',
        ],
      },
      {
        questionRef: 'medicalEmergencyRoomTreatmentFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientHospitalizedERLastTwelveMonths, [
          'Visited the ER',
        ]),
        sourceQuestionRefs: ['clientHospitalizedERLastTwelveMonths'],
      },
      {
        questionRef: 'medicalAssistanceBathingFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Bathing',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalAssistanceDressingFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Dressing',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalAssistanceWalkingFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Walking',
          'Transferring',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalAssistanceEatingFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Eating',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalAssistanceUsingToiletFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Toileting',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalAssistanceGettingUpFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Transferring',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalAssistanceTakingMedicationFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Taking medication',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalAssistanceShoppingFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Shopping',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalAssistanceCookingFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientActivitiesReceivedAssistanceList, [
          'Preparing meals',
        ]),
        sourceQuestionRefs: ['clientActivitiesReceivedAssistanceList', 'clientDifficultyWithTasks'],
      },
      {
        questionRef: 'medicalClaimDisabilityFirstInsured',
        answer: intakeSurvey?.clientDisabilityWorkersComp?.toLowerCase() === 'yes' ? 'yes' : 'no',
        sourceQuestionRefs: ['clientDisabilityWorkersComp'],
      },
      {
        questionRef: 'medicalInjuryFromFallFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, ['fall', 'falling']),
        sourceQuestionRefs: ['clientInjuriesList'],
      },
      {
        questionRef: 'medicalPhysicalTherapyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientTherapyTypes, [
          'physical therapy',
          'PT',
        ]),
        sourceQuestionRefs: ['clientTherapyTypes'],
      },
      {
        questionRef: 'medicalOccupationalTherapyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientTherapyTypes, [
          'occupational therapy',
          'OT',
        ]),
        sourceQuestionRefs: ['clientTherapyTypes'],
      },
      {
        questionRef: 'medicalSpeechTherapyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientTherapyTypes, ['speech therapy']),
        sourceQuestionRefs: ['clientTherapyTypes'],
      },
      {
        questionRef: 'medicalRespiratoryTherapyFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientTherapyTypes, [
          'respiratory therapy',
        ]),
        sourceQuestionRefs: ['clientTherapyTypes'],
      },
      {
        questionRef: 'medicalCaneUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['Cane']),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalCrutchUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['Crutches']),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalWalkerUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['Walker']),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalWheelchairUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['Wheelchair']),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalMotorizedScooterUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
          'Motorized scooter',
          'scooter',
        ]),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalMotorizedChairUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
          'Motorized chair',
          'power chair',
        ]),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalStairLiftUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['Stairlift']),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalHandicappedPlacardUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
          'Handicap placard',
          'handicapped',
        ]),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalCatheterUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['Catheter']),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalOxygenEquipmentUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
          'Oxygen',
          'oxygen equipment',
        ]),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalRespiratorUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
          'Respirator',
          'ventilator',
        ]),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalCpapUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
          'CPAP',
          'BiPAP',
          'Bi-PAP',
        ]),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalDefibrillatorUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
          'Defibrillator',
          'ICD',
        ]),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalPacemakerUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['Pacemaker']),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalDialysisUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
          'Dialysis',
          'dialysis machine',
        ]),
        sourceQuestionRefs: ['clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalNarcoticsUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, [
          'narcotics',
          'opioids',
        ]),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalBarbituratesUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['barbiturates']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalAmphetaminesUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['amphetamines']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalHallucinogensUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, [
          'hallucinogens',
          'Mushrooms',
        ]),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalHeroinUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['heroin']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalCocaineUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['cocaine']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineCigarettesUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, [
          'Cigarettes',
          'smoking',
        ]),
        sourceQuestionRefs: ['clientSubstancesUsed', 'clientSmokerEver'],
      },
      {
        questionRef: 'nicotinePipeUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['Pipes']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineCigarsUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['Cigars']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineSnuffUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['Snuff']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineChewingTobaccoUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['Chewing tobacco']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineVapingUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['Vapes']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineECigarettesUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['Vapes']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineHookahUseFirstInsured',
        answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['Hookah']),
        sourceQuestionRefs: ['clientSubstancesUsed'],
      },
    ].filter(
      update =>
        update.answer !== '' && (Array.isArray(update.answer) ? update.answer.length > 0 : true),
    );

    mappings = [...mappings, ...primaryMappings];
  }

  // Partner/second insured medical mappings
  if (clientInfo.mutableClientPartner?.intakeSurvey) {
    const partnerSurvey = clientInfo.mutableClientPartner.intakeSurvey;
    const partnerHeightData = convertHeightToFeetAndInches(partnerSurvey.clientHeightInches || 0);

    const partnerMappings = [
      // Partner height and weight
      {
        questionRef: 'medicalSecondProposedInsuredHeightFT',
        answer: partnerHeightData.feet > 0 ? partnerHeightData.feet.toString() : '',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientHeightInches'],
      },
      {
        questionRef: 'medicalSecondProposedInsuredHeightIN',
        answer: partnerHeightData.feet > 0 ? partnerHeightData.inches.toString() : '',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientHeightInches'],
      },
      {
        questionRef: 'medicalSecondProposedInsuredWeight',
        answer:
          (partnerSurvey.clientWeightPounds || 0) > 0
            ? partnerSurvey.clientWeightPounds.toString()
            : '',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientWeightPounds'],
      },

      // Partner basic demographic information
      {
        questionRef: 'secondProposedInsuredBirthDate',
        answer: formatDateForForm(partnerSurvey.clientBirthDate),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientBirthDate'],
      },
      {
        questionRef: 'secondProposedInsuredGender',
        answer: mapGender(partnerSurvey.clientGenderAtBirth),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientGenderAtBirth'],
      },
      {
        questionRef: 'secondProposedInsuredMarriedStatus',
        answer: mapMaritalStatus(partnerSurvey.clientMaritalStatus),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientMaritalStatus'],
      },

      // Partner medical conditions
      {
        questionRef: 'medicalHeartConditionSecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'cardiovascular')
          ? 'yes'
          : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalCancerSecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'cancer') ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalDiabetesSecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'diabetes') ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalLungDisorderSecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'respiratory') ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalIntestinalSecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'digestive') ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalBrainNervousSecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'neurological')
          ? 'yes'
          : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalArthritisSecondInsured',
        answer: checkDiagnosisList(
          partnerSurvey.clientDiagnosesList,
          'arthritis',
          partnerSurvey.clientInjuriesList,
        )
          ? 'yes'
          : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalEyesEarsSecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'eyes_ears') ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalKidneySecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'kidney') ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalAidsSecondInsured',
        answer: checkDiagnosisList(partnerSurvey.clientDiagnosesList, 'immune') ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalSmokerSecondInsured',
        answer: partnerSurvey.clientSmokerEver === 'Yes' ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSmokerEver'],
      },
      {
        questionRef: 'medicalHospitalPatientSecondInsured',
        answer:
          partnerSurvey.clientHospitalizedERLastTwelveMonths &&
          (partnerSurvey.clientHospitalizedERLastTwelveMonths.includes('Hospitalized') ||
            partnerSurvey.clientHospitalizedERLastTwelveMonths.includes('Visited the ER') ||
            partnerSurvey.clientPriorTwoYearHospitalizationCount > 0)
            ? 'yes'
            : 'no',
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHospitalizedERLastTwelveMonths',
          PARTNER_PREFIX + 'clientPriorTwoYearHospitalizationCount',
        ],
      },
      {
        questionRef: 'medicalPhysicalTherapySecondInsured',
        answer: checkTherapyUsage(partnerSurvey.clientTherapyTypes) ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientTherapyTypes'],
      },
      {
        questionRef: 'medicalMedicalApplianceSecondInsured',
        answer: checkMedicalApplianceUsage(partnerSurvey.clientAssistiveDevicesList) ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalMarijuanaUseSecondInsured',
        answer: checkSubstanceUsage(partnerSurvey.clientSubstancesUsed, 'marijuana') ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalHardDrugsSecondInsured',
        answer: checkSubstanceUsage(partnerSurvey.clientSubstancesUsed, 'hard_drugs')
          ? 'yes'
          : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalDrugUseSecondInsured',
        answer:
          checkSubstanceUsage(partnerSurvey.clientSubstancesUsed, 'marijuana') ||
          checkSubstanceUsage(partnerSurvey.clientSubstancesUsed, 'hard_drugs')
            ? 'yes'
            : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalTobaccoUseSecondInsured',
        answer:
          checkSubstanceUsage(partnerSurvey.clientSubstancesUsed, 'tobacco') ||
          partnerSurvey.clientSmokerEver === 'Yes'
            ? 'yes'
            : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSmokerEver'],
      },
      {
        questionRef: 'medicalMedicationsSecondInsured',
        answer: parseMedicationsToTableFormat(partnerSurvey.clientMedicationIntake),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientMedicationIntake'],
      },
      {
        questionRef: 'medicalHeartAttackSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'heart attack',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalHighBloodPressureSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDiagnosesList, [
          'high blood pressure',
          'hypertension',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalStrokeSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'Single stroke',
          'Multiple strokes',
          'stroke',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalTransientIschemicAttackTiaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'TIA ("mini stroke")',
          'TIA',
          'Transient Ischemic Attack',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalCongestiveHeartFailureSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'Congestive Heart Failure',
          'Cardiomyopathy or congestive heart failure (CHF)',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalIrregularHeartbeatSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'irregular heartbeat',
          'arrhythmia',
          'Atrial fibrillation or other arrhythmia',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalAtrialFibrillationSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'irregular heartbeat',
          'arrhythmia',
          'Atrial fibrillation or other arrhythmia',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPeripheralVascularDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'peripheral vascular disease',
          'peripheral artery disease',
          'PAD',
          'Peripheral vascular disease / aneurysm',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalHeartMurmurSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'heart murmur',
          'Valvular Heart Disease',
          'Heart valve disease / murmur',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalValvularHeartDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'heart murmur',
          'Valvular Heart Disease',
          'Heart valve disease / murmur',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalCoronaryArteryDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'heart disease',
          'heart disorder',
          'Coronary Artery Disease',
          'CAD',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalLeukemiaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientCancerTypeLocation, ['Leukemia']),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientCancerTypeLocation',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalLymphomaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientCancerTypeLocation, ['Lymphoma']),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientCancerTypeLocation',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalTumorSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientCancerTypeLocation, [
          'Tumor',
          'Mass',
          'Lump',
          'Skin Lesion',
          'Skin Disorder',
          'Skin Cancer',
          'Skin',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientCancerTypeLocation',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalAsthmaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'Asthma',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalChronicObstructivePulmonaryDiseaseCopdSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'COPD / emphysema',
          'Chronic bronchitis',
          'Chronic lung disorder',
          'Chronic obstructive pulmonary disease (COPD)',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalEmphysemaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'COPD / emphysema',
          'Emphysema',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalSleepApneaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'Sleep apnea',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalCrohnsDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          "Crohn's disease",
          "Ulcerative Colitis / Crohn's Disease (IBD)",
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalUlcerativeColitisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'colitis',
          "Ulcerative Colitis / Crohn's Disease (IBD)",
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalHepatitisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'hepatitis',
          'Hepatitis A / B / C',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPancreatitisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'pancreatitis',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalBipolarDisorderSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientPsychiatricDiagnosis, [
          'Bipolar Disorder (Manic Depression)',
          'Bipolar Disorder',
          'Bipolar',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientPsychiatricDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalDepressionSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientPsychiatricDiagnosis, [
          'Depression',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientPsychiatricDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalAnxietySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientPsychiatricDiagnosis, ['Anxiety']),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientPsychiatricDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPostTraumaticStressDisorderSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientPsychiatricDiagnosis, [
          'Post-traumatic stress disorder (PTSD)',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientPsychiatricDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalSchizophreniaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientPsychiatricDiagnosis, [
          'Schizophrenia',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientPsychiatricDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalChronicKidneyDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientGenitourinaryDiagnosis, [
          'Chronic kidney disease',
          'Kidney disorder',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientGenitourinaryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalKidneyFailureSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientGenitourinaryDiagnosis, [
          'Kidney failure',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientGenitourinaryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalAlzheimerDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientCognitiveMemoryDiagnosis, [
          "Alzheimer's",
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientCognitiveMemoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalDementiaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientCognitiveMemoryDiagnosis, [
          'Dementia',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientCognitiveMemoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalTremorsSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'Tremors',
          'Parkinson',
          'Parkinsonism',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalParkinsonDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'Parkinson',
          'Parkinsonism',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalConvulsionsEpilepsySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'Seizures',
          'Convulsions',
          'Epilepsy / seizures',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalSystemicLupusSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientImmuneSystemDiagnosis, ['Lupus']),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientImmuneSystemDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalSclerodermaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientImmuneSystemDiagnosis, [
          'Scleroderma',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientImmuneSystemDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalWetMacularDegenerationSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientEENTDiagnosis, [
          'Macular degeneration',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientEENTDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalAneurysmSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'aneurysm',
          'Peripheral vascular disease / aneurysm',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalCarotidArteryDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'carotid artery disease',
          'carotid stenosis',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalCardiomyopathySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'Cardiomyopathy',
          'Cardiomyopathy or congestive heart failure (CHF)',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalCongenitalHeartDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'congenital heart disease',
          'congenital heart defect',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalAtherosclerosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'atherosclerosis',
          'hardening of the arteries',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalVenousInsufficiencySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'venous insufficiency',
          'chronic venous insufficiency',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalDeepVeinThrombosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'deep vein thrombosis',
          'DVT',
          'blood clot',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPulmonaryEmbolismSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHeartConditionDiagnosis, [
          'pulmonary embolism',
          'PE',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientHeartConditionDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalEnlargementOfTheLymphGlandSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientCancerTypeLocation, [
          'lymph gland',
          'lymph node',
          'enlarged lymph',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientCancerTypeLocation',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalMultipleMyelomaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientCancerTypeLocation, [
          'multiple myeloma',
          'myeloma',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientCancerTypeLocation',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalAnemiaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDiagnosesList, ['anemia']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPolycythemiaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDiagnosesList, ['polycythemia']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalThrombocytopeniaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDiagnosesList, [
          'thrombocytopenia',
          'low platelet',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalPolycysticKidneyDiseaseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientGenitourinaryDiagnosis, [
          'polycystic kidney disease',
          'PKD',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientGenitourinaryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalNephritisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientGenitourinaryDiagnosis, [
          'nephritis',
          'glomerulonephritis',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientGenitourinaryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalNephroticSyndromeSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientGenitourinaryDiagnosis, [
          'nephrotic syndrome',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientGenitourinaryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalHematuriaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientGenitourinaryDiagnosis, [
          'hematuria',
          'blood in urine',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientGenitourinaryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalUrinaryIncontinenceSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientGenitourinaryDiagnosis, [
          'urinary incontinence',
          'bladder incontinence',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientGenitourinaryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalNeurogenicBladderSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientGenitourinaryDiagnosis, [
          'neurogenic bladder',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientGenitourinaryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalShortnessOfBreathSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'shortness of breath',
          'dyspnea',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPulmonaryFibrosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'pulmonary fibrosis',
          'interstitial lung disease',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalSarcoidosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'sarcoidosis',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPneumoniaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'pneumonia',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalTuberculosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientLungRespiratoryDiagnosis, [
          'tuberculosis',
          'TB',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientLungRespiratoryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalIntestinalBleedingSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'intestinal bleeding',
          'gastrointestinal bleeding',
          'GI bleed',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalUlcerSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'ulcer',
          'peptic ulcer',
          'gastric ulcer',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalGastroparesisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'gastroparesis',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalBarrettEsophagusSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'barrett',
          'esophagus',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalCirrhosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'cirrhosis',
          'liver cirrhosis',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalLiverFailureSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDigestiveDiagnosis, [
          'liver failure',
          'hepatic failure',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientDigestiveDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalSuicideAttemptSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientPsychiatricDiagnosis, [
          'suicide attempt',
          'suicidal',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientPsychiatricDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalAnorexiaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientPsychiatricDiagnosis, [
          'anorexia',
          'eating disorder',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientPsychiatricDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalChronicFatigueSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDiagnosesList, [
          'chronic fatigue',
          'CFS',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalFibromyalgiaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDiagnosesList, ['fibromyalgia']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalDizzinessSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, ['dizziness']),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalVertigoSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, ['vertigo']),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalMigrainesSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'migraine',
          'migraines',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalTraumaticBrainInjurySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'traumatic brain injury',
          'TBI',
          'head injury',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalGaitDisturbanceSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'gait disturbance',
          'walking difficulty',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalNeurofibromatosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'neurofibromatosis',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalFaintingSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'fainting',
          'syncope',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalParalysisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'paralysis',
          'paraplegia',
          'quadriplegia',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalMyastheniaGravisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'myasthenia gravis',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPeripheralNeuropathySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'peripheral neuropathy',
          'neuropathy',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalMultipleSclerosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'multiple sclerosis',
          'MS',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalALSSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'ALS',
          'amyotrophic lateral sclerosis',
          "Lou Gehrig's disease",
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalGuillainBarreSyndromeSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientBrainInjuryDiagnosis, [
          'guillain-barre',
          'guillain barre',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientBrainInjuryDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalLossOfLimbSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientInjuriesList, [
          'loss of limb',
          'amputation',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientInjuriesList'],
      },
      {
        questionRef: 'medicalOrganTransplantSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDiagnosesList, [
          'organ transplant',
          'transplant',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalMyopathySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientInjuriesList, [
          'myopathy',
          'muscle disease',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientInjuriesList',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPolymyalgiaRheumaticaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientImmuneSystemDiagnosis, [
          'polymyalgia rheumatica',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientImmuneSystemDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalMuscularDystrophySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientInjuriesList, [
          'muscular dystrophy',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientInjuriesList',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalVertebralFracturesSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientInjuriesList, [
          'vertebral fracture',
          'spinal fracture',
          'compression fracture',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientInjuriesList'],
      },
      {
        questionRef: 'medicalOsteoporosisSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientDiagnosesList, ['osteoporosis']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDiagnosesList'],
      },
      {
        questionRef: 'medicalRetinopathySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientEENTDiagnosis, [
          'retinopathy',
          'diabetic retinopathy',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientEENTDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalLossOfVisionSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientEENTDiagnosis, [
          'loss of vision',
          'blindness',
          'vision loss',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientEENTDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalKaposiSarcomaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientImmuneSystemDiagnosis, [
          'kaposi sarcoma',
          "kaposi's sarcoma",
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientImmuneSystemDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalPneumocystisCariniiPneumoniaSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientImmuneSystemDiagnosis, [
          'pneumocystis',
          'PCP',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientImmuneSystemDiagnosis',
          PARTNER_PREFIX + 'clientDiagnosesList',
        ],
      },
      {
        questionRef: 'medicalHospitalTreatmentSecondInsured',
        answer:
          partnerSurvey.clientPriorTwoYearHospitalizationCount > 0 ||
          checkForSpecificConditions(partnerSurvey.clientHospitalizedERLastTwelveMonths, [
            'Hospitalized',
          ]) === 'yes'
            ? 'yes'
            : 'no',
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientPriorTwoYearHospitalizationCount',
          PARTNER_PREFIX + 'clientHospitalizedERLastTwelveMonths',
        ],
      },
      {
        questionRef: 'medicalEmergencyRoomTreatmentSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientHospitalizedERLastTwelveMonths, [
          'Visited the ER',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientHospitalizedERLastTwelveMonths'],
      },
      {
        questionRef: 'medicalAssistanceBathingSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Bathing',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalAssistanceDressingSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Dressing',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalAssistanceWalkingSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Walking',
          'Transferring',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalAssistanceEatingSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Eating',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalAssistanceUsingToiletSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Toileting',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalAssistanceGettingUpSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Transferring',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalAssistanceTakingMedicationSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Taking medication',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalAssistanceShoppingSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Shopping',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalAssistanceCookingSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientActivitiesReceivedAssistanceList, [
          'Preparing meals',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientActivitiesReceivedAssistanceList',
          PARTNER_PREFIX + 'clientDifficultyWithTasks',
        ],
      },
      {
        questionRef: 'medicalClaimDisabilitySecondInsured',
        answer: partnerSurvey?.clientDisabilityWorkersComp?.toLowerCase() === 'yes' ? 'yes' : 'no',
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientDisabilityWorkersComp'],
      },
      {
        questionRef: 'medicalInjuryFromFallSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientInjuriesList, ['fall', 'falling']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientInjuriesList'],
      },
      {
        questionRef: 'medicalPhysicalTherapySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientTherapyTypes, [
          'physical therapy',
          'PT',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientTherapyTypes'],
      },
      {
        questionRef: 'medicalOccupationalTherapySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientTherapyTypes, [
          'occupational therapy',
          'OT',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientTherapyTypes'],
      },
      {
        questionRef: 'medicalSpeechTherapySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientTherapyTypes, ['speech therapy']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientTherapyTypes'],
      },
      {
        questionRef: 'medicalRespiratoryTherapySecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientTherapyTypes, [
          'respiratory therapy',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientTherapyTypes'],
      },
      {
        questionRef: 'medicalCaneUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, ['Cane']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalCrutchUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, ['Crutches']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalWalkerUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, ['Walker']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalWheelchairUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Wheelchair',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalMotorizedScooterUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Motorized scooter',
          'scooter',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalMotorizedChairUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Motorized chair',
          'power chair',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalStairLiftUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Stairlift',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalHandicappedPlacardUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Handicap placard',
          'handicapped',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalCatheterUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, ['Catheter']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalOxygenEquipmentUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Oxygen',
          'oxygen equipment',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalRespiratorUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Respirator',
          'ventilator',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalCpapUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'CPAP',
          'BiPAP',
          'Bi-PAP',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalDefibrillatorUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Defibrillator',
          'ICD',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalPacemakerUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, ['Pacemaker']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalDialysisUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientAssistiveDevicesList, [
          'Dialysis',
          'dialysis machine',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientAssistiveDevicesList'],
      },
      {
        questionRef: 'medicalNarcoticsUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, [
          'narcotics',
          'opioids',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalBarbituratesUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['barbiturates']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalAmphetaminesUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['amphetamines']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalHallucinogensUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, [
          'hallucinogens',
          'Mushrooms',
        ]),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalHeroinUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['heroin']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'medicalCocaineUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['cocaine']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineCigarettesUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, [
          'Cigarettes',
          'smoking',
        ]),
        sourceQuestionRefs: [
          PARTNER_PREFIX + 'clientSubstancesUsed',
          PARTNER_PREFIX + 'clientSmokerEver',
        ],
      },
      {
        questionRef: 'nicotinePipeUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['Pipes']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineCigarsUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['Cigars']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineSnuffUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['Snuff']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineChewingTobaccoUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['Chewing tobacco']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineVapingUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['Vapes']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineECigarettesUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['Vapes']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
      {
        questionRef: 'nicotineHookahUseSecondInsured',
        answer: checkForSpecificConditions(partnerSurvey.clientSubstancesUsed, ['Hookah']),
        sourceQuestionRefs: [PARTNER_PREFIX + 'clientSubstancesUsed'],
      },
    ].filter(
      update =>
        update.answer !== '' && (Array.isArray(update.answer) ? update.answer.length > 0 : true),
    );

    mappings = [...mappings, ...partnerMappings];
  }

  return mappings;
};

export const getPolicyUpdatesForOneAmerica = (
  policyFundingSource: PolicyFundingSource,
): PreFillUpdate[] => {
  let initialQuote = policyFundingSource.sourceQuote;
  if (typeof initialQuote === 'string') {
    try {
      initialQuote = JSON.parse(initialQuote);
    } catch (e) {
      console.error('Failed to parse sourceQuote:', e);
      initialQuote = null;
    }
  }

  // Type guard to check if sourceQuote is a PolicyQuote (not Traditional LTC)
  const isPolicyQuote = (quote: any): quote is PolicyQuote => {
    return quote && typeof quote === 'object' && 'premiumAmount' in quote;
  };

  const sourceQuote = isPolicyQuote(initialQuote) ? initialQuote : null;

  return [
    {
      questionRef: 'policyFaceAmount',
      answer: returnSafeString(policyFundingSource.policyMaximumBenefitAmount),
    },
    {
      questionRef: 'ltcPremiumForPolicy',
      answer: returnSafeString(sourceQuote?.premiumAmount),
    },
    {
      questionRef: 'policyProductFundingSelection',
      answer: mapProductFundingOptionToOptionId(sourceQuote?.productFundingOption),
    },
    {
      questionRef: 'policySingleReturnCob',
      answer: isSinglePremiumPolicy(sourceQuote)
        ? mapCobDurationToOptionId(sourceQuote?.cobDuration)
        : '',
    },
    {
      questionRef: 'policyRecurringPayPeriod',
      answer: isRecurringPremiumPolicy(sourceQuote)
        ? mapCobPaymentOptionToId(sourceQuote?.cobPaymentOption)
        : '',
    },
    {
      questionRef: 'policyRecurringBillingMode',
      answer: isRecurringPremiumPolicy(sourceQuote) ? 'annual' : '',
    },
    {
      questionRef: 'policyMinBenefitDurationAob',
      answer: 'aob',
    },
    {
      questionRef: 'policyMinBenefitDurationCob',
      answer: mapCobDurationToOptionId(sourceQuote?.cobDuration),
    },
    {
      questionRef: 'policyCobPaymentPeriod',
      answer: mapCobPaymentOptionToId(sourceQuote?.cobPaymentOption),
    },
    {
      questionRef: 'policyCobRecurringBilling',
      answer: isRecurringPremiumPolicy(sourceQuote) ? 'annual' : '',
    },
    {
      questionRef: 'policyInflationProtection',
      answer: mapInflationProtectionToOptionId(
        sourceQuote?.aobInflation,
        sourceQuote?.cobInflation,
      ),
    },
    {
      questionRef: 'policyInflationDuration',
      answer: mapInflationDurationToOptionId(sourceQuote?.inflationDuration),
    },
    {
      questionRef: 'policyTotalPremiumSelected',
      answer: isSinglePremiumPolicy(sourceQuote)
        ? returnSafeString(sourceQuote?.premiumAmount)
        : isRecurringPremiumPolicy(sourceQuote)
          ? returnSafeString(sourceQuote?.premiumAmount ? sourceQuote?.premiumAmount * 10 : null)
          : '',
    },
    {
      questionRef: 'ltcPremiumOneTime',
      answer: isSinglePremiumPolicy(sourceQuote)
        ? returnSafeString(sourceQuote?.premiumAmount)
        : isRecurringPremiumPolicy(sourceQuote)
          ? returnSafeString(sourceQuote?.premiumAmount ? sourceQuote?.premiumAmount * 10 : null)
          : '',
    },
    {
      questionRef: 'ltcPremiumPerYear',
      answer: isRecurringPremiumPolicy(sourceQuote)
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'ltcBuyInflationProtection',
      answer:
        mapInflationProtectionToOptionId(sourceQuote?.aobInflation, sourceQuote?.cobInflation) ===
        'none'
          ? 'no'
          : 'yes',
    },
  ].filter(update => update.answer);
};

export const getAdvisorUpdatesForOneAmerica = (
  advisorInfo: AdvisorInfoWithWritingCode,
): PreFillUpdate[] => {
  return [
    {
      questionRef: 'policyInformationProducerName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'policyInformationProducerPhoneNumberType',
      answer: 'work',
    },
    {
      questionRef: 'policyInformationProducerPhoneNumber',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'policyInformationAgreementRelationToProposedInsured',
      answer: 'Financial Advisor',
    },
    {
      questionRef: 'policyInformationAgreementPrintedName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'ltcAgentPrintedName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'representativeContactName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'representativeContactPhone',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'representativeContactEmail',
      answer: advisorInfo.advisorEmail || '',
    },
    {
      questionRef: 'patriotActProducerName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'authDisclosureRecipientName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'page13FullName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'page13CALicenseNumber',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'page13Address',
      answer: GetAdvisorAddress(advisorInfo.advisorBusinessAddress, true),
    },
    {
      questionRef: 'patriotActProducerNumber',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'representativeAgentInfo',
      answer: [
        {
          code: advisorInfo.bgaWritingCode || '',
          name: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
          percentage: 100,
        },
      ],
    },
    {
      questionRef: 'replacementProducerName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'agentFullName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'agentEmailAddress',
      answer: advisorInfo.advisorEmail || '',
    },
    {
      questionRef: 'policyInformationProducerAgencyNumber',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'agentNumber',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'authDisclosureRecipientAddress',
      answer: GetAdvisorAddress(advisorInfo.advisorBusinessAddress),
    },
    {
      questionRef: 'authDisclosureRecipientCity',
      answer: advisorInfo.advisorBusinessAddress?.city || '',
    },
    {
      questionRef: 'authDisclosureRecipientState',
      answer: advisorInfo.advisorBusinessAddress?.state || '',
    },
    {
      questionRef: 'authDisclosureRecipientZip',
      answer: advisorInfo.advisorBusinessAddress?.zipCode || '',
    },
    {
      questionRef: 'adcAgentName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'representativeOrAgent',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
  ].filter(update => update.answer);
};

export const getStaticUpdatesForOneAmerica = () => {
  return [
    {
      questionRef: 'patriotActProduct',
      answer: 'life-insurance-annuity-ltc',
    },
    {
      questionRef: 'epaTransactionType',
      answer: 'new-policy',
    },
    {
      questionRef: 'policyNonforfeitureBenefit',
      answer: 'no',
    },
  ];
};

const mapProductFundingOptionToOptionId = (productFundingOption: string | undefined): string => {
  if (!productFundingOption) return '';
  const optionLower = productFundingOption.toLowerCase();
  if (optionLower.includes('single premium')) {
    if (optionLower.includes('return') || optionLower.includes('rop')) {
      return 'single-premium-with-return';
    }
    return 'single-premium-no-return';
  }
  if (optionLower.includes('recurring')) {
    return 'recurring-premium';
  }
  if (optionLower.includes('annuity')) {
    return 'annuity-funding';
  }
  return '';
};

const mapCobDurationToOptionId = (cobDuration: string | undefined): string => {
  if (!cobDuration) return '';
  const durationMap: Record<string, string> = {
    '4 Years': '4-years',
    '2 Years': '2-years',
    '6 Years': '6-years',
    'Lifetime': 'lifetime',
    'None': 'none',
  };
  return durationMap[cobDuration] || '';
};

const mapCobPaymentOptionToId = (cobPaymentOption: string | undefined): string => {
  if (!cobPaymentOption) return '';
  const paymentOptionMap: Record<string, string> = {
    '5-Pay': '5-pay',
    '10-Pay': '10-pay',
    '20-Pay': '20-pay',
    'Pay to 95': 'pay-to-95',
    'Single Pay': 'single-pay',
  };
  return paymentOptionMap[cobPaymentOption] || '';
};

const mapInflationProtectionToOptionId = (
  aobInflation: string | undefined,
  cobInflation: string | undefined,
): string => {
  if (!aobInflation && !cobInflation) return 'none';
  const aob = aobInflation?.toLowerCase() || 'none';
  const cob = cobInflation?.toLowerCase() || 'none';
  if (aob === 'none' && cob === 'none') return 'none';

  const parseInflationValue = (value: string): string => {
    if (value === 'none') return '0';
    const match = value.match(/(\d+)%?/);
    return match ? match[1] : '0';
  };

  const aobValue = parseInflationValue(aob);
  const cobValue = parseInflationValue(cob);

  if (cob === 'none' && aobValue !== '0') {
    return `${aobValue}-no-cob`;
  }

  if (aobValue === cobValue) {
    return `${aobValue}-${cobValue}`;
  }

  return `${aobValue}-${cobValue}`;
};

const mapInflationDurationToOptionId = (inflationDuration: string | undefined): string => {
  if (!inflationDuration) return 'na';

  const durationLower = inflationDuration.toLowerCase();
  if (durationLower === 'none' || durationLower.includes('n/a')) return 'na';

  if (durationLower.includes('20')) return '20-years';

  if (durationLower.includes('lifetime')) return 'lifetime';

  return '';
};

const isSinglePremiumPolicy = (sourceQuote: PolicyQuote | null | undefined): boolean =>
  !!sourceQuote?.productFundingOption.toLowerCase().includes('single premium');

const isRecurringPremiumPolicy = (sourceQuote: PolicyQuote | null | undefined): boolean =>
  !!sourceQuote?.productFundingOption.toLowerCase().includes('recurring');

// Helper functions for direct medical mapping
const checkDiagnosisList = (
  diagnoses: string[],
  condition: string,
  injuries?: string[],
): boolean => {
  if (!diagnoses || !Array.isArray(diagnoses)) return false;

  const conditionMappings: Record<string, string[]> = {
    cardiovascular: [
      'high blood pressure',
      'heart attack, chest discomfort, heart murmur, circulatory or heart disorder',
      'heart',
      'stroke',
      'blood pressure',
      'cardiac',
      'cardiovascular',
      'hypertension',
      'heart attack',
      'angina',
      'arrhythmia',
      'circulatory',
    ],
    cancer: [
      'cancer, tumor, polyp, cyst',
      'cancer',
      'tumor',
      'leukemia',
      'lymphoma',
      'malignant',
      'carcinoma',
      'sarcoma',
      'polyp',
      'cyst',
    ],
    diabetes: [
      'diabetes',
      'thyroid disorder, elevated cholesterol, or other endocrine or metabolic disorder',
      'diabetic',
      'blood sugar',
      'insulin',
      'endocrine',
      'metabolic',
      'thyroid',
      'cholesterol',
    ],
    respiratory: [
      'asthma, shortness of breath, sleep apnea or any other lung/respiratory disorder',
      'asthma',
      'copd',
      'emphysema',
      'lung',
      'bronchitis',
      'pneumonia',
      'respiratory',
      'breathing',
      'shortness of breath',
      'sleep apnea',
    ],
    digestive: [
      'any disorder of stomach, liver, or digestive system (e.g., hepatitis, ulcer, colitis)',
      'stomach',
      'liver',
      'digestive',
      'hepatitis',
      'ulcer',
      'colitis',
      'intestinal',
    ],
    neurological: [
      'seizures, stroke, fainting, paralysis, falls, or any other disorder of brain/nervous system',
      "alzheimers disease, dementia, memory impairment, parkinson's disease, or any other progressive neurological disease",
      'alzheimer',
      'dementia',
      'parkinson',
      'stroke',
      'epilepsy',
      'seizure',
      'multiple sclerosis',
      'brain',
      'nervous',
      'neurological',
      'paralysis',
      'fainting',
    ],
    mental: [
      'emotional, nervous, or psychiatric conditions (e.g. depression, anxiety, bipolar or borderline personality disorders, schizophrenia)',
      'depression',
      'anxiety',
      'bipolar',
      'schizophrenia',
      'mental health',
      'psychiatric',
      'emotional',
      'borderline personality',
    ],
    kidney: [
      'kidney, bladder, urinary, reproductive organ, breast of prostate disorder',
      'kidney',
      'bladder',
      'urinary',
      'reproductive',
      'breast',
      'prostate',
    ],
    immune: [
      'aids, hiv, or any other disorder of the immune system',
      'aids',
      'hiv',
      'immune system',
    ],
    eyes_ears: [
      'disorder of eyes, ears, nose, throat',
      'eyes',
      'ears',
      'nose',
      'throat',
      'vision',
      'hearing',
    ],
    arthritis: [
      'chronic joint pain (e.g., arthritis, gout, bursitis)',
      "rheumatoid or any other form of arthritis, loss of limb or deformity, disorder of bone, joint, muscle, back, spine or neck, osteoporosis, systemic lupus, scleroderma, parkinson's disease, multiple sclerosis, als (amyotrophic lateral sclerosis), skin disorder, or any other disorder of the skeletal system",
      'arthritis',
      'joint pain',
      'rheumatoid',
      'osteoporosis',
      'bone disorder',
      'muscle disorder',
      'back pain',
      'spine disorder',
      'neck disorder',
      'skeletal',
      'lupus',
      'scleroderma',
      'gout',
      'bursitis',
      'joint',
      'muscle',
      'bone',
    ],
  };

  const searchTerms = conditionMappings[condition] || [];

  const foundInDiagnoses = diagnoses.some(diagnosis =>
    searchTerms.some(term => diagnosis.toLowerCase().includes(term.toLowerCase())),
  );

  let foundInInjuries = false;
  if (injuries && Array.isArray(injuries)) {
    foundInInjuries = injuries.some(injury =>
      searchTerms.some(term => injury.toLowerCase().includes(term.toLowerCase())),
    );
  }

  return foundInDiagnoses || foundInInjuries;
};

const checkTherapyUsage = (therapyTypes: string[] | string): boolean => {
  if (!therapyTypes) return false;

  const therapyList =
    typeof therapyTypes === 'string' ? therapyTypes.split('|').map(s => s.trim()) : therapyTypes;

  if (!Array.isArray(therapyList) || therapyList.length === 0) return false;

  const therapyTerms = [
    'physical therapy',
    'pt',
    'occupational therapy',
    'ot',
    'chiropractic care',
    'chiropractic',
    'speech therapy',
    'respiratory therapy',
  ];

  return therapyList.some(therapy =>
    therapyTerms.some(term => therapy.toLowerCase().includes(term.toLowerCase())),
  );
};

const checkSubstanceUsage = (substancesUsed: string[] | string, substanceType: string): boolean => {
  if (!substancesUsed) return false;

  const substanceList =
    typeof substancesUsed === 'string'
      ? substancesUsed.split('|').map(s => s.trim())
      : substancesUsed;

  if (!Array.isArray(substanceList) || substanceList.length === 0) return false;

  const substanceTerms: Record<string, string[]> = {
    marijuana: ['marijuana', 'cannabis', 'weed', 'pot'],
    hard_drugs: [
      'hard drugs',
      'narcotics',
      'barbiturates',
      'amphetamines',
      'hallucinogens',
      'heroin',
      'cocaine',
      'methamphetamine',
      'opioids',
      'fentanyl',
    ],
    tobacco: [
      'cigarettes',
      'cigars',
      'pipes',
      'tobacco',
      'snuff',
      'chewing tobacco',
      'nicotine',
      'vaping',
      'e-cigarettes',
      'hookah',
      'smoking',
    ],
  };

  const searchTerms = substanceTerms[substanceType] || [];
  return substanceList.some(substance =>
    searchTerms.some(term => substance.toLowerCase().includes(term.toLowerCase())),
  );
};
interface MedicationTableItem {
  medicalMedicationsItem: string;
}

const parseMedicationsToTableFormat = (
  medicationString: string | undefined,
): MedicationTableItem[] => {
  if (!medicationString || medicationString.trim() === '') return [];

  const medications = medicationString
    .split(/[,;|\n]/)
    .map(med => med.trim())
    .filter(med => med.length > 0)
    .slice(0, 3); // OneAmerica form only allows 3 rows

  return medications.map(medication => ({
    medicalMedicationsItem: medication,
  }));
};
